// =============================================================================
// PullToRefresh — global pull-to-refresh for mobile / PWA standalone mode.
//
// Native browser pull-to-refresh disappears once a PWA goes standalone, which
// is how operators run the app from their home screen. This component reads
// touch events on the document, fires once the pull crosses a threshold,
// and calls onRefresh — the default invalidates every TanStack query.
//
// Activates only when the page is scrolled to the top, so it does not
// conflict with normal vertical scrolling. Touch-driven only; mouse drag is
// intentionally unsupported (would interfere with text selection on desktop).
//
// "The page" is the window — or, on an inner-scroller page (a pinned header
// over a `flex-1 overflow-y-auto` list: Equipment, Yield Estimates,
// Compliance), the list that carries {...PAGE_SCROLLER}. Without the marker
// that list is just "a scroller of its own" and the pull stands down, so on
// an unmarked inner-scroller page pull-to-refresh does nothing (farm audit
// X-05, W-20). See ownScrollTakesTouch.
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { useQueryClient, onlineManager } from '@tanstack/react-query'
import { RotateCw } from 'lucide-react'
import clsx from 'clsx'

interface Props {
  /** Override what happens on pull. Default: invalidate every query. */
  onRefresh?: () => Promise<void> | void
  /** How far the operator must pull before the refresh fires. */
  threshold?: number
}

const PULL_THRESHOLD     = 80
const MAX_INDICATOR_PULL = 120

/** Spread onto an inner-scroller page's MAIN list — the `flex-1
 *  overflow-y-auto` region under a pinned header — so pull-to-refresh treats it
 *  as the page: at its top a pull refreshes, anywhere below the top it is an
 *  ordinary scroll, exactly as the window behaves. Never on a dialog body, a
 *  card's table or a map pane: those keep their own touches. */
export const PAGE_SCROLLER = { 'data-pull-to-refresh': 'page' } as const

/** Does a touch starting at `target` belong to a scroller of its own (a modal
 *  body, a long table inside a card, any nested scroll region)? Then the
 *  operator is scrolling THAT, not pulling the page, and the pull stands down —
 *  otherwise a modal refreshed the page when the operator scrolled up in it.
 *  Any scrollable ancestor wins (at its top too: native overscroll rubber-bands,
 *  which beats a page refresh from inside a dialog) — except the page's own
 *  scroller (PAGE_SCROLLER), which takes the window's rule: only its scroll
 *  position matters, and the walk carries on past it at the top. */
function ownScrollTakesTouch(target: HTMLElement | null): boolean {
  for (let el = target; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    if (scrollsOnItsOwn(el) && !isPageScrollerAtTop(el)) return true
  }
  return false
}

const SCROLLING_OVERFLOW = new Set(['auto', 'scroll', 'overlay'])

function scrollsOnItsOwn(el: HTMLElement): boolean {
  return SCROLLING_OVERFLOW.has(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight
}

function isPageScrollerAtTop(el: HTMLElement): boolean {
  return el.dataset.pullToRefresh === 'page' && el.scrollTop <= 0
}

export default function PullToRefresh({ onRefresh, threshold = PULL_THRESHOLD }: Props) {
  const qc = useQueryClient()
  const startY     = useRef<number | null>(null)
  const startX     = useRef<number>(0)
  const trackingY  = useRef<number>(0)
  const [pull,    setPull]    = useState(0)
  const [busy,    setBusy]    = useState(false)
  const [armed,   setArmed]   = useState(false)

  useEffect(() => {
    function onTouchStart(e: TouchEvent) {
      // Only engage when scrolled to the very top — otherwise the user is
      // doing a normal scroll that we mustn't intercept.
      if (window.scrollY > 0 || document.documentElement.scrollTop > 0) {
        startY.current = null
        return
      }
      // Skip if the touch is in a horizontally-scrolling element or on an
      // <input>/<textarea> (would steal focus).
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
        if (target.closest('[data-pull-to-refresh-skip="true"]')) return
        // The big one: if the user is inside a scrollable container
        // (modal body, nested card scroll, etc.) DO NOT hijack their
        // gesture as a page refresh. Was previously letting modals
        // refresh the page when the operator tried to scroll down.
        if (ownScrollTakesTouch(target)) return
      }
      startY.current = e.touches[0]?.clientY ?? null
      startX.current = e.touches[0]?.clientX ?? 0
      trackingY.current = startY.current ?? 0
    }

    function onTouchMove(e: TouchEvent) {
      if (startY.current == null || busy) return
      const touch = e.touches[0]
      const y = touch?.clientY ?? 0
      const delta = y - startY.current
      if (delta <= 0) {
        // Pulling up = normal scroll attempt; bail.
        startY.current = null
        setPull(0)
        return
      }
      const dx = Math.abs((touch?.clientX ?? startX.current) - startX.current)
      // Gesture slop: do nothing until the move is unambiguous, so we never
      // preventDefault (or show the indicator) on the first few pixels.
      if (delta <= 10 && dx <= 10) return
      if (dx > delta) {
        // Horizontally-dominant swipe (tab strip, carousel) — not a pull.
        startY.current = null
        setPull(0)
        return
      }
      // Damped pull — feels nicer than 1:1 movement.
      const damped = Math.min(MAX_INDICATOR_PULL, delta * 0.5)
      setPull(damped)
      setArmed(damped >= threshold)
      // Block native overscroll while we're displaying our indicator.
      if (e.cancelable) e.preventDefault()
    }

    async function onTouchEnd() {
      if (startY.current == null) return
      startY.current = null
      const wasArmed = armed
      setArmed(false)
      if (wasArmed && !busy) {
        setBusy(true)
        setPull(threshold)   // hold the indicator while we refresh
        try {
          if (onRefresh) {
            await onRefresh()
          } else {
            // Un-wedge react-query first: if onlineManager is stuck offline
            // (roaming Wi-Fi missed the 'online' event — see main.tsx wedge
            // guard), every query is paused and invalidateQueries() is a no-op,
            // so the operator's pull does nothing and they fall back to a full
            // browser reload. Force online (only ever TRUE) when the OS says
            // we're connected so the pull actually refetches.
            if (navigator.onLine) onlineManager.setOnline(true)
            await qc.invalidateQueries()
          }
        } finally {
          setBusy(false)
          setPull(0)
        }
      } else {
        setPull(0)
      }
    }

    // passive: false so we can preventDefault during the pull.
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove',  onTouchMove,  { passive: false })
    document.addEventListener('touchend',   onTouchEnd,   { passive: true })
    document.addEventListener('touchcancel', onTouchEnd,  { passive: true })
    return () => {
      document.removeEventListener('touchstart',  onTouchStart)
      document.removeEventListener('touchmove',   onTouchMove)
      document.removeEventListener('touchend',    onTouchEnd)
      document.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [qc, onRefresh, threshold, busy, armed])

  if (pull <= 0 && !busy) return null
  const opacity = Math.min(1, pull / threshold)
  return (
    <div
      className="fixed top-0 left-1/2 -translate-x-1/2 z-elevated pointer-events-none"
      style={{
        transform: `translate(-50%, ${Math.min(pull, MAX_INDICATOR_PULL) - 28}px)`,
        opacity,
      }}>
      <div className={clsx(
        'rounded-full surface border hairline-strong shadow-lg p-2',
        armed && 'bg-green-700 border-green-600',
      )}>
        <RotateCw size={18} className={clsx(busy ? 'animate-spin' : 'transition-transform',
          armed && !busy && 'rotate-180',
        )} />
      </div>
    </div>
  )
}
